import { Button, Checkbox, Dialog, DialogActions, DialogContent, Stack, TextField, Typography } from '@mui/material';
import { NestedProductCategoriesWithProducts } from 'features/nested-product-category/types/dtos';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SelectNestedProductDialogProps {
    isOpen: boolean;
    toggleDialog: () => void;
    initalSelectedNestedProducts?: Array<string>;
    nestedProductCategoriesWithProducts: NestedProductCategoriesWithProducts;
    onSelectNestedProductComplete: (nestedProductIds: Array<string>) => void;
}

const SelectNestedProductDialog = ({
    isOpen,
    toggleDialog,
    nestedProductCategoriesWithProducts,
    initalSelectedNestedProducts = [],
    onSelectNestedProductComplete,
}: SelectNestedProductDialogProps) => {
    const { t } = useTranslation();
    const [selectedNestedProducts, setSelectedNestedProducts] = useState<Array<string>>(initalSelectedNestedProducts);
    const [searchTerm, setSearchTerm] = useState<string>('');

    const handleSelectNestedProduct = (nestedProductId: string) => {
        setSelectedNestedProducts((prevSelected) => {
            if (prevSelected.includes(nestedProductId)) {
                return prevSelected.filter((id) => id !== nestedProductId);
            } else {
                return [...prevSelected, nestedProductId];
            }
        });
    };

    const handleMultiSelectNestedProduct = (category) => {
        const allProductIds = category.products.map((product) => product.id);
        setSelectedNestedProducts((prevSelected) =>
            prevSelected.some((id) => allProductIds.includes(id))
                ? prevSelected.filter((id) => !allProductIds.includes(id))
                : [...prevSelected, ...allProductIds],
        );
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleReset = () => {
        setSelectedNestedProducts([]);
        setSearchTerm('');
    };

    const handleAddClicked = () => {
        onSelectNestedProductComplete(selectedNestedProducts);
        handleReset();
        toggleDialog();
    };

    const handleCancelClicked = () => {
        handleReset();
        toggleDialog();
    };

    return (
        <Dialog
            sx={{
                '& .MuiPaper-root': {
                    width: '100%',
                    maxWidth: '600px',
                    height: '80vh',
                },
            }}
            open={isOpen}
            onClose={toggleDialog}
        >
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Stack flexGrow={1} gap={2}>
                    <Typography variant="h6" component="h2">
                        Select Nested Products
                    </Typography>
                    <TextField
                        value={searchTerm}
                        onChange={handleSearchChange}
                        variant="standard"
                        placeholder="Search by category..."
                        sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'white' }}
                    />
                    <Stack flexGrow={1} spacing={2}>
                        {nestedProductCategoriesWithProducts
                            .filter((category) => category.products.length > 0)
                            .filter((category) => category.name.toLowerCase().includes(searchTerm.toLowerCase()))
                            .map((category) => (
                                <Stack key={category.id} spacing={1}>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent={'space-between'}
                                        spacing={1}
                                    >
                                        <Typography variant="subtitle1">{category.name}</Typography>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Checkbox
                                                checked={selectedNestedProducts.some((id) =>
                                                    category.products.some((product) => product.id === id),
                                                )}
                                                id={`category-${category.id}`}
                                                onChange={() => handleMultiSelectNestedProduct(category)}
                                            />
                                            <Typography variant="body1">Select all</Typography>
                                        </Stack>
                                    </Stack>
                                    {category.products.map((product) => (
                                        <Stack key={product.id} direction="row" alignItems="center" spacing={1}>
                                            <Checkbox
                                                checked={selectedNestedProducts.includes(product.id)}
                                                id={`nested-product-${product.id}`}
                                                onChange={() => handleSelectNestedProduct(product.id)}
                                            />
                                            <Typography variant="body1">{product.name}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            ))}
                    </Stack>
                </Stack>
                <DialogActions sx={{ position: 'sticky', bottom: 0, padding: 2 }}>
                    <Button
                        variant="outlined"
                        sx={{
                            backgroundColor: 'background.paper',
                            '&:hover': {
                                backgroundColor: 'background.paper',
                            },
                        }}
                        onClick={handleCancelClicked}
                    >
                        {t('PRODUCT_FORM.CANCEL_BUTTON')}
                    </Button>
                    <Button color="primary" onClick={handleAddClicked}>
                        {t('PRODUCT_FORM.SAVE_BUTTON')}
                    </Button>
                </DialogActions>
            </DialogContent>
        </Dialog>
    );
};

export default SelectNestedProductDialog;
